import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { openFilePreview } from './fileUtils';

export interface FilePreviewButtonProps {
  /**
   * URL / blob URL / `data:` URI to preview. Component is a no-op
   * (renders `null`) when not provided so callers can spread the
   * button into layouts without extra conditional rendering.
   */
  src: string | null | undefined;
  /**
   * Override the label text. Defaults to the shared `c-main:crud_buttons.view`
   * translation, so callers normally don't need to set this.
   */
  label?: string;
  /**
   * Show the label text alongside the eye icon. Useful when the
   * button needs to be self-evidently a preview affordance (form
   * fields, edit modals); off by default for compact rows where
   * the icon is enough.
   */
  showLabel?: boolean;
  className?: string;
}

/**
 * Compact, opt-in "open preview" affordance — used by `FileUploader`
 * (compact + default variants) and any other surface that wants to
 * let the user preview a file regardless of whether it's a freshly-
 * dropped `File`, a saved remote URL, or an inline base64 upload.
 *
 * The button stops event propagation in its `onClick` so it can be
 * dropped inside a `react-dropzone` root without competing with the
 * dropzone's "click anywhere to open the file picker" behavior.
 *
 * The actual file-opening logic is delegated to the canonical
 * `openFilePreview` helper in `./fileUtils`, which mirrors the
 * `imageWithUploadSchema` shape (URL vs. inline `data:` upload).
 */
export function FilePreviewButton({
  src,
  label,
  showLabel = false,
  className,
}: FilePreviewButtonProps) {
  const { t } = useTranslation(['c-main']);
  const text = label ?? t('c-main:crud_buttons.view', { defaultValue: 'View' });

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (!src) return;
      e.stopPropagation();
      e.preventDefault();
      openFilePreview(src);
    },
    [src],
  );

  if (!src) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      title={text}
      aria-label={text}
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded-md shrink-0',
        'bg-primary/10 text-primary hover:bg-primary/20 transition-colors',
        showLabel ? 'px-2 h-7 text-xs font-medium' : 'size-7',
        className,
      )}>
      <EyeIcon className="size-3.5" />
      {showLabel ? <span>{text}</span> : null}
    </button>
  );
}
